<template>
    <Form ref="elFormObj" as="" v-slot="{ errors: formErrors, meta: formMeta }" :initial-values="form">
        <form @submit="submit($event)" novalidate>
            <v-alert
                v-if="showSuccessMessage"
                variant="outlined"
                class="mb-12"
            >
                <h2
                    class="line-height-1"
                >
                    Change password request has been submitted.
                </h2>
                <p>Please check your email for a change password link and further instructions.</p>
            </v-alert>

            <div
                v-show="!showSuccessMessage"
                class="text-body-1 px-0 mb-4"
            >
                <p v-if="!emailNotExists">Enter your email address and we'll send you an email with a link to reset your password.</p>
                <AlertNoEmailFound
                    v-else
                    class="mb-8"
                ></AlertNoEmailFound>

                <Field
                    name="email"
                    label="Email"
                    v-slot="{ field, errors }"
                    v-model="form.email"
                    :rules="{
                        required: true,
                        email: true
                    }"
                >
                    <v-text-field
                        v-bind="field"
                        :model-value="field.value"
                        name="email"
                        label="Email"
                        type="email"
                        autocorrect="off"
                        autocapitalize="none"
                        autocomplete="username"
                        variant="outlined"
                        :error-messages="errors"
                        :disabled="showSuccessMessage"
                        required
                    ></v-text-field>
                </Field>
            </div>

            <div
                class="px-0"
            >
                <v-btn
                    v-if="showSuccessMessage"
                    variant="text"
                    color="secondary"
                    type="submit"
                    size="large"
                    :loading="isLoadingResend"
                    @click="simulateLoading"
                    block
                >
                    Resend Link
                </v-btn>
                <v-btn
                    v-else
                    variant="elevated"
                    color="primary"
                    type="submit"
                    :disabled="!(formMeta.dirty || formMeta.touched) || !lo_isEmpty(formErrors)"
                    size="large"
                    block
                >
                    Reset Password
                </v-btn>
            </div>

        </form>
    </Form>
</template>

<script lang="ts" setup>
    import type { Field, Form } from 'vee-validate';
    import { useUser, useErrorMessage, useTracking } from '~/composables';
    import { useGlobalAlert } from 'x1-base/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import { ForgotPasswordModel } from '~/models';
    import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
    import { initVeeRules } from '~/helpers/forms';
    import AlertNoEmailFound from '~/components/alerts/AlertNoEmailFound/AlertNoEmailFound.vue';


    initVeeRules([ 'required', 'email' ]);

    const elFormObj = shallowRef<typeof Form>();
    const showSuccessMessage = ref<boolean>(false);
    const emailNotExists = ref<boolean>(false);
    const isLoadingResend = ref<boolean>(false);
    const useUserObj = useUser();
    const createGlobalAlert = useGlobalAlert();
    const { track } = useTracking();

    const forgotPasswordModel = new ForgotPasswordModel();
    const form = reactive<ForgotPasswordModel>(forgotPasswordModel);
    let loadingTimer: ReturnType<typeof setTimeout> | undefined;

    const submit = async (e: Event) => {
        e.preventDefault();
        emailNotExists.value = false;

        const validateObj = await elFormObj.value?.validate();

        if (!validateObj.valid) {
            return;
        }

        track('forgot_password', {}, {
            forgot_password_email: form.email
        });

        const payload = form.toPlainObject();
        const resp = await useUserObj.forgotPassword(payload);

        if (!lo_isError(resp)) {
            if (!resp) {
                emailNotExists.value = true;
                return;
            }

            showSuccessMessage.value = true;
        } else {
            createGlobalAlert({
                text: useErrorMessage('globalAlert.signUp.invalidCredentials'),
                show: true,
                type: 'error'
            });
        }
    };

    function simulateLoading(): void {
        isLoadingResend.value = true;
        loadingTimer = setTimeout(() => {
            isLoadingResend.value = false;
        }, 1000);
    }

    onBeforeUnmount(() => {
        clearTimeout(loadingTimer);
    });
</script>

<style lang="scss" scoped></style>
